<template>
    <a @click.prevent.stop="run"
        :class="[
            (icon || svg) ? 'mead-link fi' : 'te-link',
            isActive ? 'el--active' : '',
            disabled ? 'el--disabled' : '',
        ]">
        <template v-if="icon">
            <i :class="[icon ? `lnr-${icon}` : '']"></i>
        </template>
        <template v-if="svg">
            <div class="fi" v-html="svg"></div>
        </template>
        <span v-if="lbl">{{ lbl | athT }}</span>
        <template v-if="iconRight">
            <i v-if="iconRight" class="sm-l-xs" :class="[iconRight ? `lnr-${iconRight}` : '']"></i>
        </template>
    </a>
</template>

<script>
import { isFunction } from '@aretheyhappy/utils';

export default {
    name: 'mead-link',
    props: {
        /**
         * Name of the target vue-router route this link navigates to.
         */
        name        : {type: String, default: () => ''},
        /**
         * Alternate route name that also counts as "active" for styling purposes
         * (in addition to `name`), useful when one link should highlight for
         * several related routes.
         */
        alt         : {type: [Boolean, String], default: () => false},
        /**
         * Route params passed along when navigating to `name` (or `alt`).
         */
        params      : {type: Object, default: () => ({})},
        /**
         * Translation key for the link's visible text label, run through the
         * `athT` filter.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Optional function `({name, alt, params}) => routerPayload` used to
         * compute a custom router destination object instead of the default
         * `{name, params}` shape.
         */
        goto        : {type: [Boolean, Function], default: () => false},
        /**
         * Name of an icon (rendered via `lnr-{icon}`) shown before the label.
         */
        icon        : {type: [String, Boolean], default: () => false},
        /**
         * Name of an icon (rendered via `lnr-{iconRight}`) shown after the label.
         */
        iconRight   : {type: [String, Boolean], default: () => false},
        /**
         * Raw SVG markup rendered before the label instead of/alongside `icon`,
         * via `v-html`.
         */
        svg         : {type: [String, Boolean], default: () => false},
        /**
         * Tooltip translation key. When set, it's translated via `I18N.go` and
         * applied as the link's native `title` attribute once mounted.
         */
        title       : {type: [String, Boolean], default: () => false},
        /**
         * Disables navigation on click and applies the `el--disabled` styling.
         */
        disabled    : {type: Boolean, default: () => false},
    },
    mounted () {
        //  Add title
        if (this.title) this.$el.setAttribute('title', MeadCommon.I18N.go(this.title));

        //  Flash active
        if (this.isActive) this.$emit('isactive', this.menuPayload());
    },
    methods: {
        run: function () {
            if (this.isActive || this.disabled) return;

            //  Emit event
            this.$emit('goto', this.menuPayload());

            //  Navigate
            this.$router.push(this.routerPayload());
        },
        routerPayload: function () {
            return isFunction(this.goto)
                ? this.goto({name: this.name, alt: this.alt, params: this.params})
                : {name: this.name, params: this.params};
        },
        menuPayload: function () {
            return Object.assign({}, this.routerPayload(), {icon: this.icon, lbl: this.lbl, svg: this.svg});
        },
    },
    computed: {
        isActive: function () {
            if (this.alt) {
                if (this.$route.matched.some(({name}) => name === this.alt)) {
                    return true;
                }
            }

            return this.$route.matched.some(({name}) => name === this.name);
        },
    },
};
</script>
<style lang="scss" scoped>
@use '../../../sass/variables' as *;

.mead-link {
    display: flex;
    align-items: center;
    border: none;
    box-shadow: none;
    border-radius: 0;
    width: auto;
    font-size: $fs-s;
    font-family: $ff-b;
    font-weight: 400;
    cursor: pointer;
    padding: 0;
    position: relative;
    background-color: transparent;
    color: var(--c-txt);
    border-bottom: 1px solid var(--c-txt);
    line-height: initial;

    &.el--active,
    &:not([disabled="disabled"]):active,
    &:not([disabled="disabled"]):hover {
        &:not(.el-loader) {
            color: var(--c-txt-link);
            border-bottom-color: var(--c-txt-link);
        }
    }

    &[disabled="disabled"] {
        cursor: not-allowed;
        opacity: .5;
    }

    &:focus,
    &:active {
        outline: none;
    }

    i {
        font-size: inherit;
    }

    i + span {
        margin-left: $s-xs;
    }

    span + span {
        display: inline-block;
        margin-left: $s-xs;
    }

    //
    //  MODIFIERS
    //

    &.el--inline {
        display: inline-flex;
    }

    &.el-loader {
        cursor: default;
        border-bottom-color: transparent;
    }
}
</style>
